import type { TIssue } from "@/types/issue";
import type { TState } from "@/types/issue-metadata";

export function buildBrowsePath(
  workspaceSlug: string,
  projectIdentifier: string,
  sequenceId: string | number,
) {
  return `/${workspaceSlug}/tasks/${projectIdentifier}-${sequenceId}`;
}

/**
 * Build the browse URL for a work item.
 *
 * Events go to ``/events/<id>`` and milestones to ``/milestones/<id>`` (both
 * resolve by work-item UUID). They must NOT use the
 * ``/tasks/<identifier>-<sequenceId>`` form: the backend identifier route
 * resolves ``task__sequence_id`` only, and event/milestone sequence numbers
 * come from separate per-project counters, so their identifier collides with an
 * unrelated task's (wrong item) or matches nothing (404).
 *
 * Tasks get ``/tasks/<projectIdentifier>-<sequenceId>``, preferring the
 * issue's own ``project_identifier`` over the page-level prop. The page-level
 * prop can be stale (e.g. immediately after a create-from-template flow,
 * before the workspace project list refreshes) which produced
 * ``/tasks/ISSUE-1`` 404s; the issue payload is always authoritative for its
 * own project.
 */
export function buildBrowsePathForIssue(
  workspaceSlug: string,
  issue: Pick<TIssue, "id" | "sequence_id" | "project_identifier" | "project" | "category">,
  fallbackProjectIdentifier?: string,
) {
  if (issue.category === "event") return `/${workspaceSlug}/events/${issue.id}`;
  if (issue.category === "milestone") return `/${workspaceSlug}/milestones/${issue.id}`;
  const identifier =
    issue.project_identifier ??
    issue.project?.identifier ??
    (fallbackProjectIdentifier?.trim() ? fallbackProjectIdentifier : undefined);
  if (!identifier) return undefined;
  return `/${workspaceSlug}/tasks/${identifier}-${issue.sequence_id ?? issue.id}`;
}

/**
 * The URL the work-item full page should show for *issue* after its kind
 * changes in place (NG-2610).
 *
 * A task's ``/tasks/<identifier>-<sequenceId>`` stops resolving once it is
 * converted, so a reload or a shared link said "Work item not found". A
 * milestone's canonical ``/milestones/<id>`` renders this same page. An event's
 * canonical page is the Events detail, which has no Type control, so the page
 * keeps the event on ``/tasks/<id>`` instead: the identifier route resolves a
 * work-item UUID of any kind, so it reloads and converts back from here.
 */
export function buildWorkItemPagePath(
  workspaceSlug: string,
  issue: Pick<TIssue, "id" | "sequence_id" | "project_identifier" | "project" | "category">,
  fallbackProjectIdentifier?: string,
) {
  if (issue.category === "event") return `/${workspaceSlug}/tasks/${issue.id}`;
  return buildBrowsePathForIssue(workspaceSlug, issue, fallbackProjectIdentifier);
}

/** Canonical muted em-dash shown for an empty/unset read-only field value
 *  (status pill, dense table cell, my-work). Single source of truth so the dash
 *  doesn't drift back into a mix of "-", "—", and "None" across surfaces. Labeled
 *  contexts (a dropdown's clear option, a group-by header) use word tokens like
 *  `issue.state.none` ("No status") instead. */
export const EMPTY_VALUE = "—";

export function getIssueStateNameWithMetadata(issue: TIssue, states: TState[]): string {
  return (
    issue.state_name ??
    issue.state?.name ??
    states.find((state) => state.id === issue.state_id)?.name ??
    EMPTY_VALUE
  );
}

/** Companion to {@link getIssueStateNameWithMetadata}: the workflow status
 *  color for tinting a WorkflowStatusPill. Undefined → neutral pill. */
export function getIssueStateColorWithMetadata(
  issue: TIssue,
  states: TState[],
): string | undefined {
  return (
    issue.state?.color ?? states.find((state) => state.id === issue.state_id)?.color ?? undefined
  );
}

/** Companion to {@link getIssueStateNameWithMetadata}: the workflow status
 *  *group* (not the raw status), which is what drives a `ScheduleBar`'s colour.
 *
 *  Prefers the group denormalised onto the work item over a lookup in `states`:
 *  on workspace-scoped surfaces the rows span many projects, so the caller's
 *  single project-keyed state list can miss an item's status. Undefined →
 *  the bar falls back to its neutral tone. */
export function getIssueStateGroupWithMetadata(
  issue: TIssue,
  states: TState[],
): string | undefined {
  return (
    issue.state__group ?? states.find((state) => state.id === issue.state_id)?.group ?? undefined
  );
}

/**
 * Collapse a workflow-state list to one entry per `name`, keeping the first
 * occurrence (after any prior ordering). Defense-in-depth for the board and
 * status selectors: work-item states are workspace-scoped and *should* be unique
 * by name, but nothing at the data layer enforces that yet — a re-run seeder or
 * the create endpoint can mint a same-named duplicate, which otherwise surfaces
 * as duplicate board columns / repeated dropdown options.
 *
 * Deduping by NAME (not id) is deliberate: duplicates share a name but have
 * distinct ids, so id-dedup would not collapse them. Display surfaces only — the
 * status *settings* screen must keep showing raw rows so individual duplicates
 * stay editable/deletable.
 */
export function dedupeStatesByName<T extends { name: string }>(states: T[]): T[] {
  const seen = new Set<string>();
  const result: T[] = [];
  for (const state of states) {
    if (seen.has(state.name)) continue;
    seen.add(state.name);
    result.push(state);
  }
  return result;
}

export function parseWorkItemIdentifier(
  raw: string,
): { projectIdentifier: string; sequenceId: string } | undefined {
  const lastDash = raw.lastIndexOf("-");
  if (lastDash <= 0 || lastDash === raw.length - 1) return undefined;
  return { projectIdentifier: raw.slice(0, lastDash), sequenceId: raw.slice(lastDash + 1) };
}

export interface TParentDateBounds {
  /** ISO datetime (event parent) or YYYY-MM-DD (task parent); null side = unbounded. */
  start: string | null;
  end: string | null;
  kind: "date" | "datetime";
}

/**
 * A child work item's dates must fall inside its parent's window (backend
 * `_validate_child_within_parent`). Derive that window: tasks bound by
 * start_date/target_date, events by their event_detail datetimes.
 */
export function getParentDateBounds(parent: TIssue | null | undefined): TParentDateBounds | null {
  if (!parent) return null;
  if (parent.category === "event") {
    const start = parent.event_detail?.start_datetime ?? null;
    const end = parent.event_detail?.end_datetime ?? null;
    return start || end ? { start, end, kind: "datetime" } : null;
  }
  const start = parent.start_date ?? null;
  const end = parent.target_date ?? null;
  return start || end ? { start, end, kind: "date" } : null;
}

/** ISO 8601 → value for a `datetime-local` input (local wall time, no seconds). */
export function toLocalDateTimeInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * ISO 8601 → value for a `date` input (local wall date).
 *
 * **Caveat:** callers must pass a full ISO datetime (e.g. `"2026-06-01T00:00:00Z"`),
 * not a date-only string (`"2026-06-01"`). A date-only string is parsed as UTC
 * midnight by the JS `Date` constructor, which can shift the displayed date one
 * day earlier in UTC-negative timezones (e.g. UTC-5 renders `"2026-05-31"`).
 */
export function toLocalDateInput(iso: string | null | undefined): string {
  return toLocalDateTimeInput(iso).slice(0, 10);
}

/**
 * Pull a flat list of DRF field-error strings out of an Axios error so
 * specific validation messages (e.g. the parent-bounds rules) can be surfaced
 * inline instead of a generic banner.
 */
export function drfFieldErrorMessages(err: unknown): string[] {
  const data = (err as { response?: { data?: unknown } } | undefined)?.response?.data;
  if (!data || typeof data !== "object") return [];
  const messages: string[] = [];
  for (const value of Object.values(data as Record<string, unknown>)) {
    if (Array.isArray(value)) {
      for (const entry of value) if (typeof entry === "string") messages.push(entry);
    } else if (typeof value === "string") {
      messages.push(value);
    }
  }
  return messages;
}
